import {
	createAsyncThunk,
	createEntityAdapter,
	createSlice,
} from "@reduxjs/toolkit";
import { PendingAction, RejectedAction } from "../types";
import { aiUsageApi } from "../../apis/AI";

/* =======================
   📘 Thunks
======================= */

// Load overview stats
export const handleLoadAiOverview = createAsyncThunk(
	"aiUsage/loadOverview",
	() => aiUsageApi.getOverview()
);

// Load paginated clients usage
export const handleLoadAiClientsUsage = createAsyncThunk(
	"aiUsage/loadClients",
	(page: number = 1) => aiUsageApi.getClientsUsage(page),
	{
		condition: (page = 1, { getState }: any) => {
			const state = getState();
			if (!state.aiUsage) return true;
			return state.aiUsage.currentPage !== page;
		},
	}
);

// Load analytics (daily, model, tiers)
export const handleLoadAiAnalytics = createAsyncThunk(
	"aiUsage/loadAnalytics",
	() => aiUsageApi.getAnalytics()
);

// Load client logs
export const handleLoadAiClientLogs = createAsyncThunk(
	"aiUsage/loadClientLogs",
	({ clientId, page }: { clientId: number; page?: number }) =>
		aiUsageApi.getClientLogs(clientId, page || 1)
);

/* =======================
   📘 Adapters
======================= */
const clientsAdapter = createEntityAdapter<AiClientUsage>({
	selectId: (client) => client.id,
});

/* =======================
   📘 Slice
======================= */
const slice = createSlice({
	name: "aiUsage",
	initialState: {
		overview: null as AiOverview | null,
		topUsers: [] as AiTopUser[],
		analytics: null as AnalyticsResponse | null,
		clientsUsage: null as AiClientUsage[] | null,
		clientLogs: null as ClientsUsageResponse | null,
		...clientsAdapter.getInitialState(),
		currentPage: null as number | null,
		total: 0,

		isFetchingOverview: false,
		isFetchingClientsUsage: false,
		isFetchingAnalytics: false,
		isFetchingLogs: false,
	},
	reducers: {},
	extraReducers: (builder) => {
		builder
			/* ========== Overview ========== */
			.addCase(handleLoadAiOverview.pending, (state) => {
				state.isFetchingOverview = true;
			})
			.addCase(handleLoadAiOverview.fulfilled, (state, action) => {
				state.isFetchingOverview = false;
				state.overview = action.payload.overview;
				state.topUsers = action.payload.top_users;
			})
			.addCase(handleLoadAiOverview.rejected, (state) => {
				state.isFetchingOverview = false;
			})

			/* ========== Clients ========== */
			.addCase(handleLoadAiClientsUsage.pending, (state) => {
				state.isFetchingClientsUsage = true;
			})
			.addCase(handleLoadAiClientsUsage.fulfilled, (state, action) => {
				state.isFetchingClientsUsage = false;
				state.currentPage = action.payload.current_page;
				state.total = action.payload.total;
				state.clientsUsage = action.payload.data;
				clientsAdapter.setAll(state, action.payload.data);
			})
			.addCase(handleLoadAiClientsUsage.rejected, (state) => {
				state.isFetchingClientsUsage = false;
			})

			/* ========== Analytics ========== */
			.addCase(handleLoadAiAnalytics.pending, (state) => {
				state.isFetchingAnalytics = true;
			})
			.addCase(handleLoadAiAnalytics.fulfilled, (state, action) => {
				state.isFetchingAnalytics = false;
				state.analytics = action.payload;
			})
			.addCase(handleLoadAiAnalytics.rejected, (state) => {
				state.isFetchingAnalytics = false;
			})

			/* ========== Client Logs ========== */
			.addCase(handleLoadAiClientLogs.pending, (state) => {
				state.isFetchingLogs = true;
			})
			.addCase(handleLoadAiClientLogs.fulfilled, (state, action) => {
				state.isFetchingLogs = false;
				state.clientLogs = action.payload;
			})
			.addCase(handleLoadAiClientLogs.rejected, (state) => {
				state.isFetchingLogs = false;
			})

			/* ========== Matchers (optional) ========== */
			.addMatcher<PendingAction>(
				(action) =>
					/^aiUsage\/(loadOverview|loadClients|loadAnalytics|loadClientLogs)\/pending$/.test(
						action.type
					),
				() => {}
			)
			.addMatcher<RejectedAction>(
				(action) =>
					/^aiUsage\/(loadOverview|loadClients|loadAnalytics|loadClientLogs)\/rejected$/.test(
						action.type
					),
				() => {}
			);
	},
});

export default slice.reducer;

/* =======================
   📘 Selectors
======================= */
export const aiUsageSelectors = clientsAdapter.getSelectors(
	(state: any) => state.aiUsage
);
